import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, Map } from 'lucide-react';
import BreathingFlameMark from '../components/BreathingFlameMark';

/** Canonical flame mark (V4.6 / V6.1) — not the legacy V4.5 default. */
const FLAME_MARK_SRC = '/brand/enferno-flame-mark-v46.png';

/**
 * NotFound — branded 404. Uses the breathing flame, gives the user three
 * clear ways back, and quietly logs the failed path for debugging.
 */
const NotFound = () => {
  const { pathname } = useLocation();

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen flex items-center justify-center px-6 py-20" data-testid="not-found-page">
      <div className="max-w-xl w-full text-center">
        <div className="flex justify-center mb-6">
          <BreathingFlameMark size={140} intensity="lively" src={FLAME_MARK_SRC} />
        </div>

        <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-bold mb-3">
          404 · Path doesn't exist
        </div>
        <h1 className="font-display text-6xl md:text-7xl tracking-wide leading-none mb-4">
          That trail's gone cold.
        </h1>
        <p className="text-enf-text-secondary text-base leading-relaxed mb-8 max-w-md mx-auto">
          The page at <span className="font-mono text-enf-text-primary">{pathname}</span> doesn't exist —
          maybe a stale link, maybe a typo. Follow the embers home.
        </p>

        <div className="flex flex-col sm:flex-row justify-center gap-3 mb-8">
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors"
            data-testid="not-found-home"
          >
            <Home className="w-4 h-4" /> Home
          </Link>
          <Link
            to="/progress"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md font-semibold text-sm transition-colors"
            data-testid="not-found-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Progress
          </Link>
          <Link
            to="/roadmap"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md font-semibold text-sm transition-colors"
            data-testid="not-found-roadmap"
          >
            <Map className="w-4 h-4" /> Roadmap
          </Link>
        </div>

        <div className="text-xs text-enf-text-muted">
          Think this should exist? <a href="mailto:support@enferno.app" className="text-enf-role-info hover:text-enf-text-primary">support@enferno.app</a>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
